import { h } from "https://esm.sh/preact";
import htm from "https://esm.sh/htm";

const html = htm.bind(h);

// Helper functions for price formatting
const formatPrice = (price, isBrutto = false) => {
  if (isBrutto) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(price);
};

const calculatePricePerRoll = (price, rollenProKarton) => {
  if (!rollenProKarton || rollenProKarton === 0) return null;
  return formatPrice(price / rollenProKarton);
};

const calculateSavingsPercentage = (comparePrice, price) => {
  if (!comparePrice || !price || comparePrice <= price) return null;
  return Math.round(((comparePrice - price) / comparePrice) * 100);
};

// Cart button SVG component
const CartIcon = () => html`
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    class="size-6"
    aria-hidden="true"
  >
    <path
      d="M17 18C17.5304 18 18.0391 18.2107 18.4142 18.5858C18.7893 18.9609 19 19.4696 19 20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22C16.4696 22 15.9609 21.7893 15.5858 21.4142C15.2107 21.0391 15 20.5304 15 20C15 18.89 15.89 18 17 18ZM1 2H4.27L5.21 4H20C20.2652 4 20.5196 4.10536 20.7071 4.29289C20.8946 4.48043 21 4.73478 21 5C21 5.17 20.95 5.34 20.88 5.5L17.3 11.97C16.96 12.58 16.3 13 15.55 13H8.1L7.2 14.63L7.17 14.75C7.17 14.8163 7.19634 14.8799 7.24322 14.9268C7.29011 14.9737 7.3537 15 7.42 15H19V17H7C6.46957 17 5.96086 16.7893 5.58579 16.4142C5.21071 16.0391 5 15.5304 5 15C5 14.65 5.09 14.32 5.24 14.04L6.6 11.59L3 4H1V2ZM7 18C7.53043 18 8.03914 18.2107 8.41421 18.5858C8.78929 18.9609 9 19.4696 9 20C9 20.5304 8.78929 21.0391 8.41421 21.4142C8.03914 21.7893 7.53043 22 7 22C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20C5 18.89 5.89 18 7 18ZM16 11L18.78 6H6.14L8.5 11H16Z"
      fill="currentColor"
    />
  </svg>
`;

export function ProductCard({ product }) {
  if (!product) return null;

  // Basic product data
  const title = product.title || "";
  const bruttoPrice = (product.price || 0) / 100; // Convert cents to euros
  const nettoPrice = (product.price || 0) / 119.0; // Keep original netto calculation
  const compareAtPrice = (product.compare_at_price || 0) / 100;
  const imageUrl = product.featured_image || "";
  const handle = product.handle || "";
  const available = product.available || false;
  const firstVariantId = product.variants?.[0]?.id || "";

  // Metafields and special features
  const rollenProKarton = parseInt(product.metafields?.rollen_pro_karton) || 0;
  const pricePerRoll = calculatePricePerRoll(nettoPrice, rollenProKarton);
  const hasDiscount = compareAtPrice && compareAtPrice > nettoPrice;
  const savingsPercentage = calculateSavingsPercentage(
    compareAtPrice,
    nettoPrice,
  );
  const sepaLastschrift =
    product.metafields?.sepa_lastschrift_aufdruck || false;
  const besonderheit = product.metafields?.besonderheit || "";
  const secondImageSrc = product.metafields?.second_product_img || "";

  // Special product types
  const isOhnePhenol = handle?.includes("ohne-phenol");
  const isBpaFrei =
    handle?.includes("bpa-frei") ||
    handle?.includes("ohne-bisphenol-a") ||
    handle?.includes("ohne-bpa");

  // Button classes
  const cartButtonClasses = available
    ? "inline-flex items-center justify-center font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer bg-primary text-white hover:bg-primary-hover active:bg-primary-pressed h-10 px-4 py-2 button-label rounded-md aspect-square !p-3 js-product-card-atc-button !size-12"
    : "inline-flex items-center justify-center font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary bg-disabled text-subdued-text cursor-not-allowed h-10 px-4 py-2 button-label rounded-md aspect-square !p-3 js-product-card-atc-button !size-12";

  // Generate badges
  const generateBadges = () => {
    const badges = [];

    if (sepaLastschrift) {
      badges.push(html`
        <div class="border-primary rounded-md border-2 bg-white px-2 py-1">
          <span class="text-primary text-sm font-semibold"
            >SEPA Lastschrift</span
          >
        </div>
      `);
    }

    if (besonderheit && besonderheit.length <= 18) {
      badges.push(html`
        <div class="border-primary rounded-md border-2 bg-white px-2 py-1">
          <span class="text-primary text-sm font-semibold"
            >${besonderheit}</span
          >
        </div>
      `);
    }

    if (isOhnePhenol) {
      badges.push(html`
        <div class="border-primary rounded-md border-2 bg-white px-2 py-1">
          <span class="text-primary text-sm font-semibold">Ohne Phenol</span>
        </div>
      `);
    }

    if (isBpaFrei) {
      badges.push(html`
        <div class="border-primary rounded-md border-2 bg-white px-2 py-1">
          <span class="text-primary text-sm font-semibold">BPA-frei</span>
        </div>
      `);
    }

    return badges;
  };

  return html`
    <div
      class="group relative flex h-full max-w-[320px] flex-col items-center justify-center bg-white p-3 md:p-6"
    >
      <div class="flex w-full flex-col min-w-0">
        <!-- Mobile header with badges and wishlist -->
        <div class="flex h-[36px] w-full flex-row justify-between lg:hidden z-10">
          <div class="flex flex-row items-center gap-2 space-x-2">
            <div class="flex w-fit flex-row gap-2 space-x-2">${generateBadges()}</div>
            ${savingsPercentage
              ? html`
                  <div
                    class="border-error rounded-md border-2 bg-white px-2 py-1"
                  >
                    <span class="text-error text-sm font-semibold"
                      >-${savingsPercentage}%</span
                    >
                  </div>
                `
              : null}
          </div>
          <div
            class="gw-add-to-wishlist-product-card-placeholder js-product-card-wishlist-button absolute top-3 right-3 z-10"
            data-gw-product-id="${product.id}"
            data-gw-variant-id="${firstVariantId}"
            onclick=${(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          ></div>
        </div>

        <a href="/products/${handle}" class="flex h-full w-full flex-col">
          <div
            class="relative flex aspect-square h-full w-[120px] flex-col justify-between lg:size-[240px]"
          >
            <div class="relative size-[120px] lg:size-full">
              ${secondImageSrc
                ? html`
                    <div class="relative h-full w-full">
                      <img
                        src="${imageUrl}"
                        class="absolute inset-0 h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-0"
                        alt="${title}"
                      />
                      <img
                        src="${secondImageSrc}"
                        class="absolute inset-0 h-full w-full object-contain opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        alt="${title}"
                      />
                    </div>
                  `
                : html`
                    <img
                      src="${imageUrl}"
                      class="h-full w-full object-cover"
                      alt="${title}"
                    />
                  `}
            </div>

            <!-- Mobile cart button -->
            ${available &&
            html`
              <div class="pt-2 lg:hidden">
                <button
                  class=${cartButtonClasses}
                  data-variant-id=${firstVariantId}
                >
                  <span><${CartIcon} /></span>
                </button>
              </div>
            `}

            <!-- Desktop floating cart button -->
            ${available &&
            html`
              <div
                class="absolute right-0 -bottom-4 hidden size-12 opacity-100 duration-300 ease-in-out lg:-right-3 lg:-bottom-4 lg:block lg:opacity-0 lg:group-hover:opacity-100"
              >
                <button
                  class=${cartButtonClasses}
                  data-variant-id=${firstVariantId}
                >
                  <span><${CartIcon} /></span>
                </button>
              </div>
            `}

            <!-- Desktop badges -->
            <div class="absolute top-0 left-0 hidden lg:flex">
              <div class="flex w-fit flex-row gap-2 space-x-2">
                ${generateBadges()}
                ${savingsPercentage
                  ? html`
                      <div
                        class="border-error rounded-md border-2 bg-white px-2 py-1"
                      >
                        <span class="text-error text-sm font-semibold"
                          >-${savingsPercentage}%</span
                        >
                      </div>
                    `
                  : null}
              </div>
            </div>

            <!-- Desktop wishlist -->
            <div
              class="gw-add-to-wishlist-product-card-placeholder js-product-card-wishlist-button absolute top-0 right-0 z-10 hidden lg:block"
              data-gw-product-id="${product.id}"
              data-gw-variant-id="${firstVariantId}"
              onclick=${(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            ></div>
          </div>

          <div class="mt-4 flex flex-col gap-2 space-y-2 min-w-0 isolate">
            <!-- Reviews placeholder -->
            <div
              class="gw-rv-listing-average-placeholder block relative w-full h-[32px] min-h-[32px] overflow-hidden flex-shrink-0 mb-1"
              data-gw-product-id="${product.id}"
            ></div>

            <span class="text-base font-semibold group-hover:underline block"
              >${title}</span
            >

            <div class="flex flex-col">
              ${hasDiscount
                ? html`
                    <div class="flex items-center gap-2 space-x-2">
                      <span class="text-error font-extrabold"
                        >${formatPrice(nettoPrice)}*</span
                      >
                      <span class="text-subdued-text line-through"
                        >${formatPrice(compareAtPrice)}*</span
                      >
                    </div>
                  `
                : html`
                    <span class="font-extrabold"
                      >${formatPrice(nettoPrice)}*</span
                    >
                  `}
              <span class="text-subdued-text text-sm">
                * Inkl. USt. ${formatPrice(bruttoPrice, true)}
              </span>

              ${pricePerRoll
                ? html`
                    <span class="text-subdued-text text-sm">
                      Inhalt: ${rollenProKarton} (${pricePerRoll} / Rolle)
                    </span>
                  `
                : null}
            </div>

            <div class="flex items-center gap-2 space-x-2">
              <div class="relative flex size-6 items-center justify-center">
                <span
                  class="${available
                    ? "bg-primary"
                    : "bg-error"} size-2 rounded-full"
                ></span>
              </div>
              <span
                class="${available ? "text-primary" : "text-error"} text-sm"
              >
                ${available ? "Sofort lieferbar" : "Nicht verfügbar"}
              </span>
            </div>
          </div>
        </a>
      </div>
    </div>
  `;
}
